import { inject, Injectable } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { environment } from '@environment';

/**
 * Service responsible for implementing security features across the application.
 * This includes:
 * 1. Setting Content Security Policy (CSP) headers
 * 2. Configuring other security headers
 * 3. Providing security utilities
 */
@Injectable({
  providedIn: 'root',
})
export class SecurityService {
  private meta = inject(Meta);

  /**
   * Initialize security features for the application
   */
  initialize(): void {
    this.setupCSP();
  }

  /**
   * Set up Content Security Policy using meta tags
   * This is a client-side implementation and should be complemented with server-side headers
   */
  private setupCSP(): void {
    // Apply strict CSP in production or if explicitly enabled in development
    if (environment.production || environment.security?.strictCSPInDev) {
      // Set Content-Security-Policy
      this.meta.addTag({
        name: 'Content-Security-Policy',
        content: this.buildCSPPolicy()
      });
    } else {
      // In development with relaxed CSP, use Content-Security-Policy-Report-Only
      // This will report violations but not enforce the policy
      this.meta.addTag({
        name: 'Content-Security-Policy-Report-Only',
        content: this.buildCSPPolicy()
      });
    }

    // These headers are useful in both development and production
    // Set X-Content-Type-Options
    this.meta.addTag({
      name: 'X-Content-Type-Options',
      content: 'nosniff'
    });

    // Set X-Frame-Options
    this.meta.addTag({
      name: 'X-Frame-Options',
      content: 'SAMEORIGIN'
    });

    // Set X-XSS-Protection
    this.meta.addTag({
      name: 'X-XSS-Protection',
      content: '1; mode=block'
    });

    // Set Referrer-Policy
    this.meta.addTag({
      name: 'Referrer-Policy',
      content: 'strict-origin-when-cross-origin'
    });
  }

  /**
   * Build the Content Security Policy string
   */
  private buildCSPPolicy(): string {
    // Get allowed domains from environment config
    const allowedDomains = environment.security?.allowedDomains || [];

    // Build domain strings for different content types
    const domainString = allowedDomains.map(domain => `https://${domain}`).join(' ');

    // Build the policy
    const policy = [
      // Default policy for all content types
      "default-src 'self'",

      // Script sources
      `script-src 'self' https://js.stripe.com ${domainString}`,

      // Style sources
      `style-src 'self' 'unsafe-inline' https://fonts.googleapis.com ${domainString}`,

      // Font sources
      `font-src 'self' https://fonts.gstatic.com ${domainString}`,

      // Image sources
      `img-src 'self' data: https: ${domainString}`,

      // Connect sources (for API, WebSockets)
      `connect-src 'self' ${environment.baseUrl} ${environment.wsUrl} ${domainString}`,

      // Frame sources
      `frame-src 'self' https://js.stripe.com ${domainString}`,

      // Object sources
      "object-src 'none'",

      // Base URI restriction
      "base-uri 'self'",

      // Form action restriction
      "form-action 'self'",

      // Frame ancestors restriction
      "frame-ancestors 'self'"
    ];

    // In development, we might need to add some additional sources for debugging
    if (!environment.production) {
      // Add 'unsafe-eval' for source maps and development tools
      const scriptSrcIndex = policy.findIndex(p => p.startsWith('script-src'));
      if (scriptSrcIndex !== -1) {
        policy[scriptSrcIndex] += " 'unsafe-eval'";
      }
    }

    return policy.join('; ');
  }

  /**
   * Sanitize user input to prevent XSS attacks
   * This is a basic implementation and should be used alongside Angular's built-in sanitization
   */
  sanitizeInput(input: string): string {
    if (!input) return '';

    return input
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;')
      .replace(/\//g, '&#x2F;');
  }
}
